import type {
  NoticeCatalog,
  NoticeComposition,
  NoticeProof,
  NoticeScope,
  NoticeTerminologyNode,
} from './notice-fidelity.models';

const hash = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
const text = (value: unknown, limit = 2000): value is string =>
  typeof value === 'string' && value.trim().length > 0 && value.length <= limit;
const positive = (value: unknown): value is number =>
  Number.isSafeInteger(value) && (value as number) > 0;
const unique = (values: readonly string[]): boolean =>
  new Set(values).size === values.length;
const sameReference = (a: object, b: object): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/** Structural binding of trusted server evidence to the exact authored source. No browser vocabulary or phrase parser. */
export function matchNoticeTerminologyEvidence(
  proof: NoticeProof,
  catalog: NoticeCatalog,
  scope: NoticeScope,
): boolean {
  try {
    const source: NoticeComposition = JSON.parse(proof.sourceJson);
    const snapshot = proof.terminologySnapshot;
    if (source.contractCode !== 'notice-document-context-proof')
      return snapshot == null;
    if (
      !snapshot ||
      !catalog.documentContext ||
      !proof.contextEvidence ||
      snapshot.contractCode !== 'generated-document-terminology' ||
      snapshot.version !== 1 ||
      !hash(snapshot.semanticHash) ||
      !hash(snapshot.captureHash) ||
      !text(snapshot.resolutionUtc, 64) ||
      !snapshot.resolutionUtc.endsWith('Z') ||
      !Number.isFinite(Date.parse(snapshot.resolutionUtc)) ||
      snapshot.source?.sourceKind !== 'unsaved-synthetic-proof' ||
      snapshot.source.templateKey !== 'notice-editor' ||
      snapshot.source.templateVersionId !== null ||
      snapshot.source.sourceHash !== proof.sourceHash ||
      !Array.isArray(snapshot.source.references) ||
      snapshot.source.references.length > 256 ||
      !Array.isArray(snapshot.usages) ||
      snapshot.usages.length !== snapshot.source.references.length ||
      !Array.isArray(snapshot.nodes) ||
      snapshot.nodes.length > 512
    )
      return false;
    const context = snapshot.context;
    const scopes =
      scope.scopeTypeCode === 'global'
        ? [{ scopeTypeCode: 'global', scopeKey: null }]
        : [
            { scopeTypeCode: scope.scopeTypeCode, scopeKey: scope.scopeKey },
            { scopeTypeCode: 'global', scopeKey: null },
          ];
    if (
      !context ||
      context.purposeCode !== 'administration-preview' ||
      context.sourceCode !== 'administration-preview' ||
      context.localeCode !== 'en-us' ||
      context.channelCode !== 'document' ||
      context.audienceCode !== null ||
      context.tenantId !==
        (scope.scopeTypeCode === 'tenant' ? scope.scopeKey : null) ||
      context.jurisdictionCode !== null ||
      context.programCode !== null ||
      JSON.stringify(context.scopePrecedence) !== JSON.stringify(scopes) ||
      context.dimensions?.['selectedScopeTypeCode'] !== scope.scopeTypeCode ||
      context.dimensions?.['selectedScopeKey'] !== scope.scopeKey ||
      context.dimensions?.['terminologyContextPurpose'] !==
        'administration-preview' ||
      context.dimensions?.['terminologyContextSource'] !==
        'administration-preview' ||
      context.dimensions?.['tenantId'] !== context.tenantId
    )
      return false;
    const expected = source.blocks.flatMap((block, i) =>
      (block.runs ?? []).flatMap((run, j) =>
        run.kind === 'term'
          ? [
              {
                nodeId: block.id,
                fieldPath: `$.blocks[${i}].runs[${j}]`,
                alias: run.value,
              },
            ]
          : [],
      ),
    );
    // The original profile fallback remains the v1 default. A persisted registration may
    // explicitly witness a different fallback without changing that retained profile contract.
    for (const captured of proof.contextEvidence.termEvidence) {
      const registered = catalog.documentContext.terms.find(
        (t) => t.code === captured.code,
      );
      if (!registered) return false;
      const extra = captured.registeredFallbackText;
      if (
        extra !== undefined &&
        (!captured.usedFallback || !text(extra) || captured.text !== extra)
      )
        return false;
      if (
        captured.usedFallback &&
        captured.text !== (extra ?? registered.fallbackText)
      )
        return false;
    }
    const references = snapshot.source.references;
    if (
      references.length !== expected.length ||
      !unique(references.map((r) => r.usageId))
    )
      return false;
    for (const reference of references) {
      const at = expected.find(
        (r) =>
          r.nodeId === reference.nodeId && r.fieldPath === reference.fieldPath,
      );
      const registered = catalog.documentContext.terms.find(
        (t) => t.code === at?.alias,
      );
      if (
        !at ||
        !registered ||
        !/^generated-document\.[a-f0-9]{32}$/.test(reference.usageId) ||
        reference.configurationCode !== registered.configurationCode ||
        reference.tokenCode !== registered.tokenCode ||
        reference.contentPolicyCode !== 'live-label'
      )
        return false;
    }
    if (
      !unique(references.map((r) => `${r.nodeId}\0${r.fieldPath}`)) ||
      !unique(snapshot.nodes.map((n) => n.key))
    )
      return false;
    const nodes = new Map(snapshot.nodes.map((n) => [n.key, n]));
    for (const node of snapshot.nodes) {
      if (
        !text(node.configurationCode, 240) ||
        !text(node.tokenCode, 240) ||
        node.key !== `${node.configurationCode}/${node.tokenCode}` ||
        !['concept', 'form', 'phrase', 'message', 'concept-form'].includes(
          node.definitionKindCode,
        ) ||
        !text(node.registeredFallbackText) ||
        !text(node.rawContentText, 8000) ||
        !text(node.text) ||
        !text(node.reasonCode, 512) ||
        typeof node.classificationCode !== 'string' ||
        node.classificationCode.length > 160 ||
        [
          'legal',
          'legally-controlled',
          'controlled-content',
          'security',
          'security-message',
        ].includes(node.classificationCode) ||
        (node.familyCode !== null && !text(node.familyCode, 240)) ||
        (node.grammaticalFormCode !== null &&
          !text(node.grammaticalFormCode, 240)) ||
        (node.definitionKindCode === 'form' &&
          (!node.familyCode || !node.grammaticalFormCode)) ||
        !Array.isArray(node.directDependencyKeys) ||
        node.directDependencyKeys.length > 64 ||
        !unique(node.directDependencyKeys) ||
        node.directDependencyKeys.some(
          (k) =>
            !nodes.has(k) ||
            nodes.get(k)!.configurationCode !== node.configurationCode,
        )
      )
        return false;
      if (node.usedFallback === true) {
        if (
          (node.definitionId !== null && !positive(node.definitionId)) ||
          node.text !== node.registeredFallbackText ||
          node.rawContentText !== node.registeredFallbackText ||
          node.publicationId !== null ||
          node.revisionId !== null ||
          node.revisionContentHash !== null ||
          node.directDependencyKeys.length
        )
          return false;
      } else if (
        node.usedFallback !== false ||
        node.definitionKindCode === 'concept-form' ||
        !positive(node.definitionId) ||
        !positive(node.publicationId) ||
        !positive(node.revisionId) ||
        !positive(node.configurationVersionId) ||
        !text(node.configurationVersionCode, 160) ||
        !text(node.cacheRevisionToken, 512) ||
        !hash(node.revisionContentHash) ||
        !scopes.some(
          (s) =>
            s.scopeTypeCode === node.matchedScopeTypeCode &&
            s.scopeKey === node.matchedScopeKey,
        )
      )
        return false;
    }
    const reached = new Set<string>();
    const usageIds = new Set<string>();
    for (const usage of snapshot.usages) {
      const reference = references.find(
        (r) => r.usageId === usage.reference?.usageId,
      );
      const node = nodes.get(usage.nodeKey);
      if (
        !reference ||
        !sameReference(reference, usage.reference) ||
        usageIds.has(reference.usageId) ||
        !node ||
        usage.nodeKey !==
          `${reference.configurationCode}/${reference.tokenCode}` ||
        usage.text !== node.text ||
        usage.usedFallback !== node.usedFallback ||
        usage.reasonCode !== node.reasonCode ||
        !Array.isArray(usage.dependencyKeys)
      )
        return false;
      usageIds.add(reference.usageId);
      const dependencies = closure(node.key, nodes);
      if (
        !dependencies ||
        JSON.stringify(dependencies) !== JSON.stringify(usage.dependencyKeys)
      )
        return false;
      reached.add(node.key);
      dependencies.forEach((k) => reached.add(k));
      const at = expected.find(
        (r) =>
          r.nodeId === reference.nodeId && r.fieldPath === reference.fieldPath,
      )!;
      const captured = proof.contextEvidence.termEvidence.find(
        (t) => t.code === at.alias,
      );
      const registered = catalog.documentContext.terms.find(
        (t) => t.code === at.alias,
      )!;
      if (
        !captured ||
        captured.text !== usage.text ||
        captured.usedFallback !== usage.usedFallback ||
        captured.reasonCode !== usage.reasonCode ||
        captured.configurationVersionId !== node.configurationVersionId ||
        captured.configurationVersionCode !== node.configurationVersionCode ||
        captured.cacheRevisionToken !== node.cacheRevisionToken ||
        captured.matchedScopeTypeCode !== node.matchedScopeTypeCode ||
        captured.matchedScopeKey !== node.matchedScopeKey
      )
        return false;
      if (
        usage.usedFallback &&
        node.registeredFallbackText !==
          (captured.registeredFallbackText ?? registered.fallbackText)
      )
        return false;
      if (proof.referenceEvidence) {
        const blockIndex = source.blocks.findIndex(
          (b) => b.id === reference.nodeId,
        );
        const runIndex = Number(
          reference.fieldPath.match(/\.runs\[(\d+)\]$/)?.[1],
        );
        const referenceIndex =
          source.blocks[blockIndex]
            .runs!.slice(0, runIndex + 1)
            .filter((r) => r.kind !== 'literal').length - 1;
        const rendered = proof.referenceEvidence.find(
          (r) =>
            r.blockId === reference.nodeId &&
            r.referenceIndex === referenceIndex,
        );
        if (
          !rendered ||
          rendered.kind !== 'term' ||
          rendered.code !== at.alias ||
          rendered.text !== usage.text
        )
          return false;
      }
    }
    return reached.size === nodes.size;
  } catch {
    return false;
  }
}

function closure(
  key: string,
  nodes: ReadonlyMap<string, NoticeTerminologyNode>,
): string[] | null {
  const result = new Set<string>();
  const finished = new Set<string>();
  const visit = (
    current: string,
    active: ReadonlySet<string>,
    depth: number,
  ): boolean => {
    if (active.has(current) || depth > 8) return false;
    if (finished.has(current)) return true;
    const node = nodes.get(current);
    if (!node) return false;
    const next = new Set(active);
    next.add(current);
    for (const dependency of node.directDependencyKeys) {
      if (!visit(dependency, next, depth + 1)) return false;
      result.add(dependency);
    }
    finished.add(current);
    return result.size <= 512;
  };
  return visit(key, new Set(), 0) ? [...result].sort() : null;
}
